<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LandingScreen from '../master/LandingScreen.vue';
import { useI18n } from 'vue-i18n';

const selectedProject = ref(null);
const selectedProjectId = ref(null);
const selectedImage = ref(null);
const isFullscreen = ref(false);
const isSidebarVisible = ref(false);
const screenWidth = ref(window.innerWidth);
const loading = ref(true);

const route = useRoute();
const router = useRouter();
const { locale, t, tm } = useI18n();

// متغيرات حالة تحميل الصور
const isMainImageLoading = ref(true);
const thumbnailImageLoading = ref({});

// تحديث عرض الشاشة عند تغيير الحجم
window.addEventListener('resize', () => {
    screenWidth.value = window.innerWidth;
});

// دالة لجلب مسار الصورة الرئيسية
const getMainImageUrl = (id) => {
  return new URL(`../../assets/Backend/projects/project${id}/main.webp`, import.meta.url).href;
};

// دالة لجلب صور المعرض
const getGalleryImages = (id) => {
  const images = [];
  for (let i = 1; i <= 3; i++) {
    images.push(new URL(`../../assets/Backend/projects/project${id}/${i}.webp`, import.meta.url).href);
  }
  return images;
};

// جلب البيانات من ملفات الترجمة
const allProjects = computed(() => {
  const data = tm('projectsData');
  if (Array.isArray(data)) {
    return data.map(project => ({
      ...project,
      main_image: getMainImageUrl(project.id),
      images: getGalleryImages(project.id)
    }));
  }
  return [];
});

// دالة لجلب تفاصيل مشروع محدد
function goToProject(projectId) {
    console.log('Fetching project details for ID:', projectId);
    selectedProjectId.value = Number(projectId);
    loading.value = true;
    
    const project = allProjects.value.find(p => p.id == projectId);

    if (project) {
        selectedProject.value = project;
        selectedImage.value = selectedProject.value.images ? selectedProject.value.images[0] : null;
        router.push(`/projects/${projectId}`);
        
        // تهيئة حالة تحميل الصورة الرئيسية
        isMainImageLoading.value = true;

        // تهيئة حالات تحميل الصور المصغرة
        thumbnailImageLoading.value = {};
        if (selectedProject.value.images) {
            selectedProject.value.images.forEach((image) => {
                thumbnailImageLoading.value[image] = true;
            });
        }
    } else {
        console.error('Project not found');
        selectedProject.value = null;
    }
    
    loading.value = false;
    isSidebarVisible.value = false;
}

// إضافة مستمع للوحة المفاتيح عند تحميل المكون
onMounted(() => {
    window.addEventListener('keydown', handleKeyPress);
});

// إزالة المستمع عند تدمير المكون لتجنب تسرب الذاكرة
onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyPress);
});

// دالة التعامل مع ضغطات المفاتيح
function handleKeyPress(event) {
    if (event.key === 'Escape' && isFullscreen.value) {
        toggleFullscreen(); // الخروج من وضع الشاشة الكاملة عند الضغط على Esc
    }
}

function toggleSidebar() {
    isSidebarVisible.value = !isSidebarVisible.value;
}

function selectImage(image) {
    if (image) {
        selectedImage.value = image;
        isMainImageLoading.value = true;
    } else {
        console.error('Selected image is null or undefined');
    }
}

function toggleFullscreen() {
    isFullscreen.value = !isFullscreen.value;
}

function prevImage() {
    if (!selectedProject.value || !selectedProject.value.images) return;
    const index = selectedProject.value.images.indexOf(selectedImage.value);
    if (index > 0) {
        selectedImage.value = selectedProject.value.images[index - 1];
        isMainImageLoading.value = true;
    }
}

function nextImage() {
    if (!selectedProject.value || !selectedProject.value.images) return;
    const index = selectedProject.value.images.indexOf(selectedImage.value);
    if (index < selectedProject.value.images.length - 1) {
        selectedImage.value = selectedProject.value.images[index + 1];
        isMainImageLoading.value = true;
    }
}

// جلب المشاريع عند تحميل الصفحة
onMounted(() => {
    // fetchProjects is now computed, just check route
    const projectId = route.params.id;
    if (projectId) {
        goToProject(projectId);
    } else if (allProjects.value.length > 0) {
        // Optional: Select first project if none selected
        // goToProject(allProjects.value[0].id);
        loading.value = false;
    } else {
        loading.value = false;
    }
});

// مراقبة تغيير معرف المشروع في الرابط
watch(() => route.params.id, (newId) => {
    if (newId) {
        goToProject(newId);
    }
});

// مراقبة تغيير اللغة
watch(locale, () => {
    if (selectedProjectId.value) {
        goToProject(selectedProjectId.value);
    }
});
</script>

<template>
    <div class="flex">
        <!-- زر فتح وإغلاق الـ Sidebar -->
        <button @click="toggleSidebar" class="custom-btn" :class="{ 'is-sidebar-closed': !isSidebarVisible }">
            <div class="flex flex-col items-center rounded">
                <font-awesome-icon icon="fa-solid fa-building" class="fa-icon" />
                <p class="hidden md:block">{{ t('projects.title') }}</p>
            </div>
        </button>

        <!-- Sidebar لعرض قائمة المشاريع -->
        <div
            :class="['fixedSidebar top-0 left-0 h-full bg-white dark:bg-gray-900 text-white p-4 transition-transform',
                { 'transform translate-x-0': isSidebarVisible, 'transform -translate-x-full': !isSidebarVisible }
            ]">
            <h2 class="text-4xl font-bold my-10 text-center text-primary">{{ t('projects.title') }}</h2>
            <nav class="space-y-4">
                <button v-for="project in allProjects" :key="project.id" @click="goToProject(project.id)"
                    :class="['w-full p-4 rounded-lg text-center bg-secondary text-xl opacity-45',
                        selectedProjectId === project.id ?
                        'bg-gray-400 text-white' :
                        'bg-gray-700 hover:bg-gray-600'
                    ]">
                    {{ project.name }}
                </button>
            </nav>
        </div>

        <!-- Container يعرض تفاصيل المشروع -->
         <div class="flex-1 p-4 lg:p-8 mt-8 lg:mt-18">
            <!-- حالة التحميل -->
            <div v-if="loading" class="flex justify-center items-center h-full">
                <div class="text-xl font-semibold">
                    <LandingScreen />
                </div>
            </div>

            <!-- تفاصيل المشروع عند توفرها -->
             <div v-else-if="selectedProject"
    class="project-box mx-auto p-5 lg:p-8 bg-white dark:bg-[#5b5b5b] rounded-xl shadow-xl max-w-4xl lg:max-w-6xl">

    <div class="flex flex-col-reverse lg:flex-row gap-6 lg:gap-10 items-stretch mb-8">
        
        <div class="w-full lg:w-1/2 flex flex-col">
            <h2 class="text-3xl font-bold text-[#B99269] mb-4 leading-tight">
                {{ selectedProject.name }}
            </h2>

            <div class="prose dark:prose-invert max-w-none mb-6 flex-grow">
                <p class="text-gray-700 dark:text-gray-200 leading-relaxed text-base">
                    {{ selectedProject.description }}
                </p>
            </div>

            <div class="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700 p-4 mt-auto">
                <div class="grid grid-cols-2 gap-4 divide-x divide-gray-200 dark:divide-gray-700">
                    <div class="text-center px-2">
                        <h3 class="text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-1">
                            {{ t('projects.start') }}
                        </h3>
                        <p class="font-medium text-gray-900 dark:text-white">
                            {{ selectedProject.starting_date }}
                        </p>
                    </div>
                    <div class="text-center px-2">
                        <h3 class="text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-1">
                            {{ t('projects.end') }}
                        </h3>
                        <p class="font-medium text-gray-900 dark:text-white">
                            {{ selectedProject.ending_date }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="w-full lg:w-1/2">
            <div class="relative w-full h-full min-h-[300px] lg:min-h-[400px] overflow-hidden rounded-lg shadow-md bg-gray-100 dark:bg-gray-800 group">
                <div class="cursor-pointer h-full" @click="toggleFullscreen">
                    <div v-if="isMainImageLoading"
                        class="absolute inset-0 flex items-center justify-center z-10 bg-gray-100 dark:bg-gray-800">
                        <div class="h-10 w-10 border-4 border-t-4 border-secondary rounded-full animate-spin"></div>
                    </div>

                    <img v-if="selectedImage" 
                        :src="selectedImage" 
                        loading="lazy" 
                        :alt="selectedProject.name"
                        class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        @load="isMainImageLoading = false" 
                        @error="isMainImageLoading = false"
                        @loadstart="isMainImageLoading = true" />
                </div>
            </div>
        </div>
    </div>

    <div class="w-full border-t border-gray-100 dark:border-gray-700 pt-6">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wider">
            Project Gallery
        </h3>
        
        <div class="flex flex-wrap gap-3">
            <div v-for="(image, index) in selectedProject.images" :key="index" 
                 class="relative h-20 w-20 lg:h-24 lg:w-24 flex-shrink-0">
                
                <div v-if="thumbnailImageLoading[image]"
                    class="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-md z-10">
                    <div class="h-5 w-5 border-2 border-t-2 border-secondary rounded-full animate-spin"></div>
                </div>

                <img :src="image" 
                    :alt="`${selectedProject.name} - ${index + 1}`"
                    @click="selectImage(image)"
                    :class="[
                        'w-full h-full object-cover rounded-md cursor-pointer transition-all duration-200',
                        selectedImage === image ? 'border-2 border-[#B99269] opacity-100 ring-2 ring-[#B99269]/20 shadow-lg scale-105' : 'opacity-80 hover:opacity-100 hover:scale-105'
                    ]"
                    loading="lazy"
                    @load="thumbnailImageLoading[image] = false"
                    @error="thumbnailImageLoading[image] = false"
                    @loadstart="thumbnailImageLoading[image] = true" />
            </div>
        </div>
    </div>

</div>
            



            <!-- Fullscreen Image View -->
            <div v-if="isFullscreen" class="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center"
                style="z-index: 1000;" @click.self="toggleFullscreen">
                <img :src="selectedImage" alt="Fullscreen Image"
                    class="max-w-[95%] max-h-[95%] rounded-lg shadow-lg" />
                <button class="absolute top-4 right-4 bg-transparent text-white text-3xl font-bold"
                    @click="toggleFullscreen">×</button>
                <button
                    class="absolute top-1/2 left-4 transform -translate-y-1/2 bg-black bg-opacity-70 text-white text-3xl font-bold px-2 py-1"
                    @click="prevImage" style="direction: ltr;">❮</button>
                <button
                    class="absolute top-1/2 right-4 transform -translate-y-1/2 bg-black bg-opacity-70 text-white text-3xl font-bold px-2 py-1"
                    @click="nextImage" style="direction: ltr;">❯</button>
            </div>

            <!-- رسالة في حال عدم وجود مشروع محدد -->
            <div v-else-if="!loading && !selectedProject" class="text-center my-8">
                <p class="text-gray-600">No project selected.</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Fullscreen Image View */
.fullscreen-image {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999999999999999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
}

.fullscreen-image img {
    max-width: 95%;
    max-height: 95%;
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
}

/* Sidebar تنسيقات */
.fixedSidebar {
    position: fixed;
    top: 11.5%;
    left: 0;
    height: 90vh;
    width: 16rem;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    z-index: 50;
    overflow-y: auto;
    --sb-track-color: #34505d;
    --sb-thumb-color: #314351;
    --sb-size: 5px;
}

.fixedSidebar::-webkit-scrollbar {
    width: var(--sb-size);
}

.fixedSidebar::-webkit-scrollbar-track {
    background: var(--sb-track-color);
    border-radius: 10px;
}

.fixedSidebar::-webkit-scrollbar-thumb {
    background: var(--sb-thumb-color);
    border-radius: 10px;
    border: 8px solid #c7a887;
}

@supports not selector(::-webkit-scrollbar) {
    .fixedSidebar {
        scrollbar-color: var(--sb-thumb-color) var (--sb-track-color);
    }
}

:root {
    --sb-track-color: #c7a887;
    --sb-thumb-color: #314351;
    --sb-size: 5px;
}

/* زر فتح وإغلاق الـ Sidebar */
button {
    border: none;
    cursor: pointer;
    padding: 0.5rem 0.75rem;
}

button:hover {
    opacity: 0.9;
}

button.is-sidebar-closed {
    padding: 0.5rem;
    position: fixed;
    top: 15%;
    left: 4%;
    z-index: 100;
    background-color: #BB936A;
    color: white;
    border-radius: 20px;
    text-align: center;
}

.project-box {
    box-shadow: 1px 3px 35px 0px #293340;
    padding: 20px;
    margin-top: 100px;
    margin-bottom: 50px;
}

.dark .project-box {
    box-shadow: 1px 3px 35px 0px #BB936A;
}

button.active {
    background-color: #4a4a4a;
    color: white;
}

.custom-btn {
    position: fixed;
    top: 16px;
    left: 16px;
    width: 100px;
    height: 50px;
    border-radius: 8px;
    font-size: 18px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    z-index: 50;
}

.fa-icon {
    font-size: 24px;
    margin-bottom: 5px;
}

/* شاشات الجوال */
@media (max-width: 768px) {
    .custom-btn {
        width: 50px;
        height: 50px;
    }

    .fa-icon {
        font-size: 20px;
    }
}

/* شاشات العرض الكبيرة */
@media (min-width: 768px) {
    .custom-btn {
        width: 120px;
        height: 70px;
        left: 16px;
        top: 16px;
    }

    .fa-icon {
        font-size: 28px;
    }
}

/* عنصر تحميل الصورة */
img {
    position: relative;
}

.relative {
    position: relative;
}

.absolute {
    position: absolute;
}
</style>
